import { gql } from "@apollo/client";
import { Form, Formik, FormikHelpers } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import * as yup from "yup";

import {
  makeStyles,
  FormGroup,
  FormHelperText,
  Card,
  CardHeader,
  CardContent,
  MenuItem
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { useAdmin, useTenant } from "src/components/LoggedInAdminProvider";
import RouteLeavingGuard from "src/components/RouteLeavingGuard";
import FormikCallingHoursFields from "src/components/form/FormikCallingHoursFields";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikSwitchField from "src/components/form/FormikSwitchField";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import {
  campaignEditBasics,
  campaignEditBasicsVariables
} from "src/graphql/generated/campaignEditBasics";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { AmdSetting, CampaignStatus } from "src/graphql/generated/globalTypes";
import { loggedInInfo_loggedInInfo_tenant } from "src/graphql/generated/loggedInInfo";
import { useFeatureFlag } from "src/util/featureFlags";
import useMutation from "src/util/useMutation";
import { endTime, startTime } from "src/util/yupValidators";

import BandwidthVoiceWarning from "./BandwidthVoiceWarning";
import { useCampaignEditEvents } from "./CampaignEditorsProvider";
import { validateQuestionsWithIVR } from "./Questions";
import { FormikSaveButtons } from "./SaveButtons";

const EDIT_CAMPAIGN_BASICS = gql`
  mutation campaignEditBasics(
    $campaignId: ID!
    $name: String!
    $description: String
    $callingHours: CampaignCallingHoursInput!
    $amdSetting: AmdSetting!
    $ivr: Boolean!
    $customer_profile_sid: String
    $approved_stir_shaken_sid: String
    $approved_voice_integrity_sid: String
    $approved_branded_calling_sid: String
    $approved_branded_calling_basic_sid: String
  ) {
    campaignEditBasics(
      id: $campaignId
      basics: {
        name: $name
        description: $description
        callingHours: $callingHours
      }
    ) {
      id
      name
      description
      callingHours {
        timezone
        startTime
        endTime
      }
    }
    campaignSetAmdSetting(id: $campaignId, setting: $amdSetting) {
      id
      amdSetting
    }
    campaignSetIvr(id: $campaignId, ivr: $ivr) {
      id
      ivr
    }
    campaignEditTrustProductFields(
      id: $campaignId
      customer_profile_sid: $customer_profile_sid
      approved_stir_shaken_sid: $approved_stir_shaken_sid
      approved_voice_integrity_sid: $approved_voice_integrity_sid
      approved_branded_calling_sid: $approved_branded_calling_sid
      approved_branded_calling_basic_sid: $approved_branded_calling_basic_sid
    ) {
      id
      customer_profile_sid
      approved_stir_shaken_sid
      approved_voice_integrity_sid
      approved_branded_calling_sid
      approved_branded_calling_basic_sid
    }
  }
`;

type Values = {
  name: string;
  description: string;
  timezone: string;
  startTime: string;
  endTime: string;
  amdSetting: AmdSetting;
  ivr: boolean;
  customer_profile_sid: string;
  approved_stir_shaken_sid: string;
  approved_voice_integrity_sid: string;
  approved_branded_calling_sid: string;
  approved_branded_calling_basic_sid: string;
  goToNext?: boolean;
};

const schemaForTenantAndCampaign = (
  tenant: loggedInInfo_loggedInInfo_tenant,
  campaign: getCampaign_campaign
) =>
  yup.object().shape({
    name: yup.string().required("Required"),
    description: yup.string(),
    timezone: yup.string().required("Required"),
    startTime: startTime(),
    endTime: endTime(),
    amdSetting: yup.string(),
    ivr: yup
      .bool()
      .test(
        "questions-are-compatible-with-ivr",
        "All questions must be compatible with phone-only mode before enabling",
        async (ivr?: boolean) =>
          !ivr || (await validateQuestionsWithIVR(tenant, campaign))
      )
  });

const useStyles = makeStyles(theme => ({
  switches: {
    marginBottom: theme.spacing(3)
  },
  warning: {
    marginBottom: theme.spacing(3)
  }
}));

export default function Basics({
  campaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  goToNextSection: () => void;
}): JSX.Element {
  const admin = useAdmin()!;
  const tenant = useTenant()!;
  const adminsCanToggleAmd = useFeatureFlag("adminsCanToggleAmd");
  const adminsCanTogglePhoneOnlyMode = useFeatureFlag(
    "adminsCanTogglePhoneOnlyMode"
  );
  const disablePhoneOnlyMode = campaign.status !== CampaignStatus.DRAFT;

  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [editCampaignBasics] = useMutation<
    campaignEditBasics,
    campaignEditBasicsVariables
  >(EDIT_CAMPAIGN_BASICS, {
    onErrorSnackbarMessage: "Could not edit campaign"
  });

  const sendEditEvent = useCampaignEditEvents();

  const handleSave = async (
    {
      name,
      description,
      timezone,
      startTime,
      endTime,
      amdSetting,
      ivr,
      customer_profile_sid,
      approved_stir_shaken_sid,
      approved_voice_integrity_sid,
      approved_branded_calling_sid,
      approved_branded_calling_basic_sid,
      goToNext
    }: Values,
    { setSubmitting, resetForm }: FormikHelpers<Values>
  ) => {
    const { errors, data } = await editCampaignBasics({
      variables: {
        campaignId: campaign.id,
        name,
        description,
        callingHours: {
          startTime: startTime,
          endTime: endTime,
          timezone: timezone
        },
        amdSetting,
        ivr,
        customer_profile_sid,
        approved_stir_shaken_sid,
        approved_voice_integrity_sid,
        approved_branded_calling_sid,
        approved_branded_calling_basic_sid
      }
    });

    setSubmitting(false);
    if (!errors) {
      sendEditEvent();
      resetForm({
        values: {
          name: data!.campaignEditBasics.name,
          description: data!.campaignEditBasics.description || "",
          amdSetting: data!.campaignSetAmdSetting.amdSetting,
          ivr: data!.campaignSetIvr.ivr,
          ...data!.campaignEditBasics.callingHours,
          customer_profile_sid:
            data!.campaignEditTrustProductFields.customer_profile_sid || "",
          approved_stir_shaken_sid:
            data!.campaignEditTrustProductFields.approved_stir_shaken_sid || "",
          approved_voice_integrity_sid:
            data!.campaignEditTrustProductFields.approved_voice_integrity_sid ||
            "",
          approved_branded_calling_sid:
            data!.campaignEditTrustProductFields.approved_branded_calling_sid ||
            "",
          approved_branded_calling_basic_sid:
            data!.campaignEditTrustProductFields
              .approved_branded_calling_basic_sid || ""
        }
      });
      if (goToNext) {
        goToNextSection();
      } else {
        enqueueSnackbar("Saved campaign", { variant: "info" });
      }
    }
  };

  return (
    <Formik
      initialValues={{
        name: campaign.name,
        description: campaign.description || "",
        amdSetting: campaign.amdSetting,
        ivr: campaign.ivr,
        ...campaign.callingHours,
        customer_profile_sid: campaign.customer_profile_sid || "",
        approved_stir_shaken_sid: campaign.approved_stir_shaken_sid || "",
        approved_voice_integrity_sid:
          campaign.approved_voice_integrity_sid || "",
        approved_branded_calling_sid:
          campaign.approved_branded_calling_sid || "",
        approved_branded_calling_basic_sid:
          campaign.approved_branded_calling_basic_sid || ""
      }}
      onSubmit={handleSave}
      validationSchema={schemaForTenantAndCampaign(tenant, campaign)}
    >
      {props => (
        <Form>
          <RouteLeavingGuard when={props.dirty} />
          <h2>Basics</h2>
          <FormikErrorBanner />
          <BandwidthVoiceWarning campaign={campaign} />
          <FormikTextField fullWidth label="Name" name="name" />
          <FormikTextField
            fullWidth
            multiline
            label="Description"
            name="description"
          />

          <FormikCallingHoursFields label="Calling Hours" />
          {(admin.isStaff ||
            adminsCanToggleAmd ||
            adminsCanTogglePhoneOnlyMode) && (
            <FormGroup className={classes.switches}>
              {(admin.isStaff || adminsCanToggleAmd) && (
                <FormikSelect
                  name="amdSetting"
                  label="Answering Machine Detection Mode"
                >
                  <MenuItem key={AmdSetting.DEFAULT} value={AmdSetting.DEFAULT}>
                    Default (connect only likely humans)
                  </MenuItem>
                  <MenuItem
                    key={AmdSetting.CONSERVATIVE}
                    value={AmdSetting.CONSERVATIVE}
                  >
                    Conservative (connect likely humans and possible humans)
                  </MenuItem>
                  <MenuItem
                    key={AmdSetting.DISABLED}
                    value={AmdSetting.DISABLED}
                  >
                    Disabled (connect every call)
                  </MenuItem>
                </FormikSelect>
              )}
              {(admin.isStaff || adminsCanTogglePhoneOnlyMode) &&
                campaign.provider !== "bandwidth" && (
                  <>
                    <FormikSwitchField
                      name="ivr"
                      label="Enable Phone-Only Mode"
                      switchProps={{ disabled: disablePhoneOnlyMode }}
                    />
                    {props.errors.ivr && (
                      <Alert severity="error">{props.errors.ivr}</Alert>
                    )}
                    <FormHelperText>
                      Enable callers to make calls using only their phone, and
                      answer questions on the phone’s keypad. Enabling
                      Phone-Only Mode restricts the type of questions that can
                      be used. Phone-Only Mode cannot be changed once a campaign
                      has started.
                    </FormHelperText>
                  </>
                )}
            </FormGroup>
          )}
          {admin.isStaff && (
            <Card className={classes.switches}>
              <CardHeader title="Staff Trust Product Controls" />
              <CardContent>
                <FormGroup>
                  <FormikTextField
                    name="customer_profile_sid"
                    label="Twilio Customer Profile SID"
                    placeholder="BU.."
                  />
                  <FormikTextField
                    name="approved_stir_shaken_sid"
                    label="Approved Trust Product SID for STIR/SHAKEN"
                    placeholder="BU.."
                  />
                  <FormikTextField
                    name="approved_voice_integrity_sid"
                    label="Approved Trust Product SID for Voice Integrity Beta"
                  />
                  <FormikTextField
                    name="approved_branded_calling_sid"
                    label="Approved Trust Product SID for Branded Calling"
                    placeholder="BU.."
                  />
                  <FormikTextField
                    name="approved_branded_calling_basic_sid"
                    label="Approved Trust Product SID for Branded Calling Basic"
                    placeholder="BU.."
                  />
                </FormGroup>
              </CardContent>
            </Card>
          )}
          <FormikSaveButtons continueWithoutSaving={goToNextSection} />
        </Form>
      )}
    </Formik>
  );
}
